import React, { useCallback, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { SkillBiteEditSlice, AppSlice } from '../../../../../reducers'
import { Box, Typography } from '@mui/material'
import { ActionButton } from '@emerald-works-nova/components/dist/components'
import { SkillBiteViewer } from '@emerald-works-nova/content-viewer'
import { PAGE_BLOCK_TYPES } from '../../../build/utils/page-block-types'
import { LaunchRounded } from '@mui/icons-material'
import { EmptyIndicatorMessage } from '../../../build/components/empty-indicator'
import makeStyles from '@mui/styles/makeStyles'
import { getBuildHeight } from '../../../build/utils/get-build-height'
import { buildToolkitUrl } from '../../../../../helpers/toolkit'
import { CONSTANTS } from '../../../../../util/constants'

const useStyles = makeStyles(() => ({
  pageContainer: {
    overflowY: 'auto',
    maxHeight: getBuildHeight(),
    height: getBuildHeight(),
    margin: 0,
    padding: '8px 0px 0px 0px',
    /* Firefox */
    scrollbarWidth: 'thin',
    scrollbarColor: '#A6A6A6 #EBEBEB',
    scrollBehavior: 'smooth',
    scrollPaddingRight: '4px',
    paddingRight: '4px',
    listStyle: 'none',
    '&::-webkit-scrollbar': {
      width: '8px',
      marginRight: '2px',
      marginLeft: '2px'
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '5px',
      backgroundColor: '#EBEBEB'
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '5px',
      backgroundColor: '#A6A6A6'
    }
  }
}))

const PagePreview = () => {
  const classes = useStyles()
  const skillBite = useSelector(SkillBiteEditSlice.selectors.selectSkillBite)
  const { id } = skillBite
  const selectedSession = useSelector(SkillBiteEditSlice.selectors.selectSelectedSession)
  const selectedPage = useSelector(SkillBiteEditSlice.selectors.selectSelectedPage)
  const envInfo = useSelector(AppSlice.selectors.selectEnvInfo)
  const baseToolkitUrl = buildToolkitUrl(CONSTANTS.SB_PREVIEW_TENANT_KEY(envInfo.stage), envInfo.stage, envInfo.tld)

  const pageContainerRef = useRef()
  const resetPageContainerScroll = useCallback(() => {
    if (pageContainerRef && pageContainerRef.current) {
      pageContainerRef.current.scrollTop = 0
    }
  }, [])

  const blocksWithFakeCommitment = useMemo(() => {
    resetPageContainerScroll()
    if (!selectedPage || !selectedPage.blocks) return []
    return selectedPage.blocks.map((block) => {
      if (block.type === PAGE_BLOCK_TYPES.COMMITMENT_CHECK) {
        return {
          ...block,
          commitment: {
            commitment: '<THIS IS A FAKE COMMITMENT MESSAGE>'
          }
        }
      }
      return block
    })
  }, [selectedPage, resetPageContainerScroll])

  const handlePreview = () => {
    window.open(`${baseToolkitUrl}/skill-bites/${id}/${selectedSession.id}?preview=true`, '_blank')
  }

  if (!selectedPage) {
    return (
      <EmptyIndicatorMessage
        title='No pages yet'
        subtitle='You can create pages on the Build tab'
      />
    )
  }

  return (
    <Box>
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{
          borderBottom: '1px solid #E0E0E0',
          width: '100%'
        }}
      >
        <Typography variant='body2' data-test='skill-bite-preview-text'>
          <strong>How it will look like:</strong>
        </Typography>
        <ActionButton
          dataTest='skill-bite-preview-session-button'
          sx={{ minWidth: 120, marginBottom: '10px', gap: '8px' }}
          onClick={handlePreview}
        >
          See Session on Mind Tools
          <LaunchRounded fontSize='small' />
        </ActionButton>
      </Box>
      <Box className={classes.pageContainer} ref={pageContainerRef}>
        <SkillBiteViewer
          key={selectedPage.id}
          blocks={blocksWithFakeCommitment}
          readOnly
          isAuthenticated
        />
      </Box>
    </Box>
  )
}

export default PagePreview
